import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, {
  coreErrorMessage,
  formatMinor,
} from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { loading: false, error: null, notice: '' };
let loadPromise = null;
let loadedOnce = false;

const STATUS_COPY = {
  available: 'AVAILABLE',
  owned: 'OWNED',
  pending: 'CONFIG PENDING',
  locked_stage: 'STAGE LOCKED',
  locked_wallet: 'WALLET NEEDED',
  locked_previous: 'REACH PRIOR TIER',
  insufficient_cash: 'NEED CASH',
  insufficient_favor: 'NEED FAVOR',
};

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function catalogSnapshot() {
  const session = useCoreSession.getState();
  const catalog = session.catalog;
  const items = Array.isArray(catalog?.items) ? catalog.items : [];
  const coat = catalog?.coat ?? {};
  return {
    session,
    catalog,
    items,
    cashMinor: Number(catalog?.cashMinor ?? 0),
    favor: Number(catalog?.favor?.balance ?? 0),
    capacity: Number(coat.capacity ?? session.state?.inventoryCapacity ?? 0),
    nextCapacity: coat.nextCapacity ?? null,
    walletLinked: Boolean(catalog?.walletLinked),
    stage: String(catalog?.evolution?.stage ?? '--').toUpperCase(),
    notice: catalog?.notice ?? 'TOMMY IS RESTOCKING',
  };
}

export async function loadCoat({ force = false } = {}) {
  if (force) loadedOnce = false;
  if (loadPromise) return loadPromise;
  if (loadedOnce && !force) return useCoreSession.getState().catalog;
  loadPromise = useCoreSession
    .getState()
    .loadCatalog()
    .then((catalog) => {
      loadedOnce = true;
      state.error = null;
      return catalog;
    })
    .catch((error) => {
      state.error = coreErrorMessage(error);
      loadedOnce = true;
      return null;
    })
    .finally(() => {
      loadPromise = null;
    });
  return loadPromise;
}

export function _resetCoatState() {
  loadPromise = null;
  loadedOnce = false;
  state.loading = false;
  state.error = null;
  state.notice = '';
}

function priceLabel(item) {
  const cash = `$${formatMinor({ amountMinor: item?.price?.cashMinor ?? 0 }).replace('$', '')}`;
  const favor = Number(item?.price?.favor ?? 0);
  return favor > 0 ? `${cash} + ${favor} FAVOR` : cash;
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // COAT', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'TOMMY // QUARTERMASTER', 216, 5, TERMINAL.TEXT_DIM);
  text(
    ctx,
    session.status === 'online' ? 'LIVE' : 'OFFLINE',
    width - 64,
    5,
    session.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM,
  );
}

function drawSummary(ctx, x, y, w, h) {
  const snapshot = catalogSnapshot();
  DopeUI.Panel(ctx, x, y, w, h, 'LEDGER');
  const pad = x + 12;
  text(ctx, `CASH   ${formatMinor({ amountMinor: snapshot.cashMinor })}`, pad, y + 30, TERMINAL.TEXT);
  text(ctx, `FAVOR  ${snapshot.favor}`, pad, y + 46, TERMINAL.TEXT_BRIGHT);
  text(
    ctx,
    `CAPACITY  ${snapshot.capacity}${snapshot.nextCapacity ? ` > ${snapshot.nextCapacity}` : ''}`,
    pad,
    y + 62,
    TERMINAL.TEXT_DIM,
  );
  text(ctx, `EVOLUTION // ${snapshot.stage}`, pad, y + 82, TERMINAL.TEXT_DIM);
  text(
    ctx,
    snapshot.walletLinked ? 'WALLET // LINKED' : 'WALLET // NOT LINKED',
    pad,
    y + 98,
    snapshot.walletLinked ? TERMINAL.GREEN : TERMINAL.TEXT_BRIGHT,
  );
  const feedback =
    state.error || state.notice || snapshot.session.receipt?.detail || snapshot.notice;
  text(
    ctx,
    String(feedback).slice(0, 30),
    pad,
    y + 116,
    state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM,
  );

  const online = snapshot.session.status === 'online';
  const canJob = online && !state.loading && !snapshot.session.catalogLoading;
  DopeUI.Button(
    ctx,
    x + 12,
    y + h - 42,
    w - 24,
    26,
    state.loading ? 'WORKING' : 'RUN TOMMY JOB',
    canJob ? 'hover' : 'disabled',
  );
  if (canJob) {
    interactiveRegistry.register({
      id: 'coat-job',
      x: x + 12,
      y: y + h - 42,
      w: w - 24,
      h: 26,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        state.notice = '';
        try {
          const result = await useCoreSession.getState().runTommyJob();
          const job = result?.job ?? {};
          state.notice = `FAVOR +${job.favorEarned ?? 0} // BALANCE ${job.favorBalance ?? 0}`;
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

function drawItem(ctx, item, x, y, w, online, rowH = 32) {
  const status = String(item.status || 'available');
  const compact = rowH <= 26;
  ctx.strokeStyle = status === 'available' ? TERMINAL.GREEN : TERMINAL.GREEN_DARK;
  ctx.strokeRect(x, y, w, rowH - 4);
  const title = `${String(item.kind || '').toUpperCase()} // ${item.name}`;
  text(ctx, title.slice(0, 30), x + 8, y + (compact ? 2 : 4), TERMINAL.TEXT);
  const price = `${priceLabel(item)} // ${STATUS_COPY[status] || status.toUpperCase()}`;
  text(ctx, price.slice(0, 34), x + 8, y + (compact ? 11 : 20), status === 'available' ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);

  const canBuy = online && item.purchasable && !state.loading;
  DopeUI.Button(ctx, x + w - 76, y + (compact ? 3 : 7), 68, compact ? 17 : 22, item.purchasable ? 'BUY' : '--', canBuy ? 'hover' : 'disabled');
  if (canBuy) {
    interactiveRegistry.register({
      id: `coat-buy-${item.itemId}`,
      x: x + w - 76,
      y: y + (compact ? 3 : 7),
      w: 68,
      h: compact ? 17 : 22,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        state.notice = '';
        try {
          const result = await useCoreSession.getState().buyCatalogItem(item.itemId);
          state.notice = `${result?.purchase?.name ?? item.name} SECURED`;
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
  return rowH;
}

function drawCatalog(ctx, x, y, w, h) {
  const snapshot = catalogSnapshot();
  DopeUI.Panel(ctx, x, y, w, h, 'TOMMY // STOCK');
  if (!snapshot.catalog) {
    text(
      ctx,
      state.error || 'SYNCING LEDGER. TOMMY IS COUNTING.',
      x + 12,
      y + 34,
      state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM,
    );
    return;
  }
  const online = snapshot.session.status === 'online';
  const rowH = h < 240 ? 24 : 38;
  let cursor = y + 30;
  snapshot.items.forEach((item) => {
    cursor += drawItem(ctx, item, x + 8, cursor, w - 16, online, rowH);
  });
}

registerView('coat', function renderCoat(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  const session = useCoreSession.getState();
  if (session.status === 'online' && !session.catalog && !loadPromise) {
    loadCoat().catch(() => {});
  }

  drawTop(ctx, safe.width);
  const mobile = safe.width < 520;
  if (mobile) {
    drawSummary(ctx, 8, 32, safe.width - 16, 112);
    drawCatalog(ctx, 8, 150, safe.width - 16, Math.max(150, safe.height - 202));
  } else {
    drawSummary(ctx, 8, 36, 210, 300);
    drawCatalog(ctx, 226, 36, safe.width - 234, 300);
  }

  const footerY = safe.height - 32;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, 32);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TOMMY SELLS. VINNIE COLLECTS.', 8, footerY + 8, TERMINAL.TEXT_DIM);
  const backW = mobile ? safe.width - 16 : 160;
  const backX = mobile ? 8 : safe.width - backW - 8;
  DopeUI.Button(ctx, backX, footerY + 4, backW, 22, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'coat-back',
    x: backX,
    y: footerY + 4,
    w: backW,
    h: 22,
    onClick: () => navigateTo('main_menu'),
  });
});
